import {Outlet, useNavigate} from 'react-router-dom';
import {Suspense, useState} from 'react';
import {ActionIcon} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {AiOutlineLayout, AiOutlineLogout} from 'react-icons/ai';
import {
    PlatformAppShell,
    PlatformFooter,
    PlatformHeader,
    PlatformHeaderToolbar,
    PlatformLoader,
    PlatformNavbarMode,
} from '@flexinet/ui-components';
import {PortalNavbar} from './menu-items/pages/PortalNavbar';
import {useUserAttributes} from '../hooks/useUserAttributes';
import config from '../config';

// Authenticated shell. Header with user menu + sidebar navbar (PortalNavbar
// reads the menu items + filters them by Verified Permissions allowedActions).
// Mirrors flexi-bot-app/src/layout/UserLayout.tsx.
//
// Apps customize:
//  - Menu items in src/layout/menu-items/data/menus.tsx
//  - Toolbar buttons (e.g. upgrade button, docs link) — add via additionalToolbarItems
//  - portalUrl for cross-app links (set in src/config/platform.ts)
const NAVBAR_WIDTH_MEDIUM = 310;
const NAVBAR_WIDTH_COMPACT = 70;

export const UserLayout = () => {
    const navigate = useNavigate();
    const [mode, setMode] = useState(PlatformNavbarMode.MEDIUM);
    const [navbarWidth, setNavbarWidth] = useState(NAVBAR_WIDTH_MEDIUM);
    const [opened, {toggle}] = useDisclosure();

    const {givenName, familyName, email} = useUserAttributes();

    const userInfo = givenName
        ? {name: [givenName, familyName].filter(Boolean).join(' '), email}
        : undefined;

    const additionalMenuItems = [
        {
            title: 'Sign Out',
            link: '/sign-out',
            onClick: () => navigate('/sign-out'),
            icon: <AiOutlineLogout />,
        },
    ];

    const toolbar = (
        <PlatformHeaderToolbar
            user={userInfo}
            portalUrl={config.platform?.portal}
            additionalMenuItems={additionalMenuItems}
            additionalToolbarItems={[
                <ActionIcon
                    key="navbar-toggle"
                    size="lg"
                    variant="subtle"
                    title="Toggle Navbar Layout"
                    onClick={toggle}
                >
                    <AiOutlineLayout size="1.8em" />
                </ActionIcon>,
            ]}
        />
    );

    const header = <PlatformHeader toolbar={toolbar} />;

    const changeMode = (newMode: PlatformNavbarMode) => {
        setMode(newMode);
        setNavbarWidth(newMode === PlatformNavbarMode.MEDIUM ? NAVBAR_WIDTH_MEDIUM : NAVBAR_WIDTH_COMPACT);
    };

    return (
        <PlatformAppShell
            platformNavbar={<PortalNavbar mode={mode} changeMode={changeMode} />}
            platformHeader={header}
            platformFooter={<PlatformFooter />}
            header={{height: 80}}
            footer={{height: 100}}
            navbar={{
                width: navbarWidth,
                breakpoint: 'sm',
                collapsed: {desktop: opened, mobile: !opened},
            }}
        >
            <Suspense fallback={<PlatformLoader />}>
                <Outlet />
            </Suspense>
        </PlatformAppShell>
    );
};
